import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Settings, RotateCcw, Save, AlertTriangle } from 'lucide-react';

export default function SettingsModule() {
  const { settings, setSettings, resetAllData, brokerData, priceData, realTrades, transactionsData } = useApp();
  const [local, setLocal] = useState({ ...settings });
  const [showReset, setShowReset] = useState(false);

  const handleSave = () => {
    setSettings({
      ...local,
      maxPrice: parseFloat(local.maxPrice) || 5,
      minLiquidity: parseFloat(local.minLiquidity) || 0.1,
      simCapital: parseFloat(local.simCapital) || 500000,
      riskPerTrade: parseFloat(local.riskPerTrade) || 0.01,
      maxPositions: parseInt(local.maxPositions) || 3,
      maxExposure: parseFloat(local.maxExposure) || 0.4,
      stopLossPercent: parseFloat(local.stopLossPercent) || 0.05,
      timeStopDays: parseInt(local.timeStopDays) || 20,
    });
  };

  const handleReset = () => {
    resetAllData();
    setShowReset(false);
    window.location.reload();
  };

  const fields = [
    { key: 'maxPrice', label: 'Max Price (PHP)', desc: 'Only show stocks at or below this price', step: '0.5' },
    { key: 'minLiquidity', label: 'Min Liquidity Score', desc: '0-1 scale, filters low liquidity stocks', step: '0.05' },
    { key: 'simCapital', label: 'Simulator Capital (PHP)', desc: 'Starting capital for paper trading', step: '10000' },
    { key: 'riskPerTrade', label: 'Risk Per Trade', desc: 'Fraction of capital risked per trade (e.g. 0.01 = 1%)', step: '0.005' },
    { key: 'maxPositions', label: 'Max Positions', desc: 'Maximum concurrent open positions', step: '1' },
    { key: 'maxExposure', label: 'Max Exposure', desc: 'Maximum portfolio exposure (e.g. 0.4 = 40%)', step: '0.05' },
    { key: 'stopLossPercent', label: 'Stop Loss %', desc: 'Default stop loss percentage (e.g. 0.05 = 5%)', step: '0.01' },
    { key: 'timeStopDays', label: 'Time Stop (days)', desc: 'Auto-exit after N days if no movement', step: '1' },
  ];

  // Calculate localStorage usage
  const storageUsed = (() => {
    try {
      let total = 0;
      for (const key in localStorage) {
        if (key.startsWith('bf4_')) {
          total += localStorage.getItem(key)?.length || 0;
        }
      }
      return (total / 1024 / 1024).toFixed(2);
    } catch { return '0'; }
  })();

  return (
    <div className="p-6 max-w-[900px] mx-auto" data-testid="settings-module">
      <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2 mb-1">
        <Settings className="w-6 h-6 text-trading-blue" />
        Settings
      </h1>
      <p className="text-sm text-trading-muted mb-6">Configure trading parameters and risk management</p>

      {/* Settings Form */}
      <div className="bg-trading-surface border border-trading-border rounded-sm p-5 mb-6">
        <div className="space-y-4">
          {fields.map(f => (
            <div key={f.key} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start">
              <div className="md:col-span-2">
                <label className="text-sm text-trading-text font-medium block">{f.label}</label>
                <span className="text-xs text-trading-muted">{f.desc}</span>
              </div>
              <Input
                data-testid={`setting-${f.key}`}
                type="number"
                step={f.step}
                value={local[f.key]}
                onChange={e => setLocal(prev => ({ ...prev, [f.key]: e.target.value }))}
                className="bg-trading-bg border-trading-border text-trading-text h-8 font-mono text-right"
              />
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={handleSave} data-testid="save-settings-btn" className="bg-trading-blue hover:bg-trading-blue/90 text-white rounded-sm">
            <Save className="w-4 h-4 mr-2" /> Save Settings
          </Button>
        </div>
      </div>

      {/* Storage Info */}
      <div className="bg-trading-surface border border-trading-border rounded-sm p-5 mb-6">
        <h3 className="text-xs uppercase tracking-widest text-trading-muted font-semibold mb-3">Data Storage</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          <StorageItem label="Broker Rows" value={brokerData.length.toLocaleString()} />
          <StorageItem label="Price Rows" value={priceData.length.toLocaleString()} />
          <StorageItem label="Transactions" value={(transactionsData?.length || 0).toLocaleString()} />
          <StorageItem label="Real Trades" value={realTrades.length.toString()} />
          <StorageItem label="Storage Used" value={`${storageUsed} MB`} />
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-trading-red/5 border border-trading-red/20 rounded-sm p-5">
        <h3 className="text-sm font-medium text-trading-red flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4" /> Danger Zone
        </h3>
        <p className="text-xs text-trading-muted mb-3">
          Reset all data including uploaded CSVs, broker tags, settings, trades, and watchlist. This cannot be undone.
        </p>
        <Button
          variant="ghost"
          onClick={() => setShowReset(true)}
          data-testid="reset-all-btn"
          className="border border-trading-red/30 text-trading-red hover:bg-trading-red/10 rounded-sm"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Reset All Data
        </Button>
      </div>

      {/* Reset Confirmation */}
      <Dialog open={showReset} onOpenChange={setShowReset}>
        <DialogContent className="bg-trading-surface border-trading-border" data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-trading-red">Confirm Reset</DialogTitle>
            <DialogDescription className="text-trading-muted">
              This will permanently delete all your data. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReset(false)} className="text-trading-muted border border-trading-border rounded-sm">Cancel</Button>
            <Button onClick={handleReset} data-testid="confirm-reset-btn" className="bg-trading-red hover:bg-trading-red/90 text-white rounded-sm">
              Reset Everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StorageItem({ label, value }) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-widest text-trading-muted block">{label}</span>
      <span className="font-mono text-sm text-trading-text">{value}</span>
    </div>
  );
}
